/**
 * useGuardSocket — assina o GuardHub (real-time watch) via WebSocket.
 *
 * Substitui o poll de 5s do GuardLiveCard: enquanto o componente esta montado,
 * abre uma WS pra /api/ws/monitor, manda { op:'watch', charId } e chama onPos
 * pra cada frame { t:'pos', ... } que a .so streama. Fecha (unwatch) no unmount.
 *
 * Reconecta com backoff (500ms -> 8s). Token JWT vai na query (browser nao seta
 * Authorization em WS). Zero storage: o historico continua vivo so no browser.
 */
import { useEffect, useRef } from 'react';

const WS_BASE = (import.meta.env.VITE_API_URL || 'https://api.caballiberty.com/api')
  .replace(/^http/, 'ws');   // https:// -> wss://  (http:// -> ws://)

function authToken(): string {
  try {
    return JSON.parse(localStorage.getItem('liberty_auth') || '{}').token || '';
  } catch {
    return '';
  }
}

export type GuardPosFrame = {
  t: 'pos' | 'rich';
  charId: number;
  userNum: number;
  mapId: number;
  x: number;
  y: number;
  alz: number;
  speed: number;
  bikeSpeed: number;
  ch: number;
  ts: number;
};

// [BACKFILL] ponto do historico continuo (POSBATCH ~1/s) guardado na API.
export type GuardHistPoint = {
  ts: number;       // epoch ms (parede, atribuido na API)
  mapId: number;
  x: number;
  y: number;
  speed: number;
  ch?: number;
};

// Alerta do MoveGuard (speed/macro) — campos numericos variam por kind.
export type GuardServerAlert = {
  kind: 'speed' | 'macro' | string;
  charIdx?: number;
  mapId?: number;
  ts?: number;
  name?: string | null;
  observedUps?: number;
  sustainedUps?: number;
  ratio?: number;
  speedStat?: number;
  areaSpan?: number;
  movingFrac?: number;
  revisit?: number;
  windowSec?: number;
  uniqueCells?: number;
  samples?: number;
};

export type GuardHistMsg = {
  t: 'hist';
  charId: number;
  points: GuardHistPoint[];
  alerts: GuardServerAlert[];
};

export function useGuardSocket(
  charId: number,
  onPos: (f: GuardPosFrame) => void,
  opts?: {
    rich?: boolean;
    // [BACKFILL] chega logo apos o watch: historico + alertas ja acumulados
    onHist?: (h: GuardHistMsg) => void;
    // alerta do MoveGuard ao vivo enquanto a tela esta aberta
    onAlert?: (a: GuardServerAlert) => void;
  },
) {
  // refs pros callbacks: evita reconectar a WS quando a identidade deles muda
  const onPosRef = useRef(onPos);
  onPosRef.current = onPos;
  const onHistRef = useRef(opts?.onHist);
  onHistRef.current = opts?.onHist;
  const onAlertRef = useRef(opts?.onAlert);
  onAlertRef.current = opts?.onAlert;
  const rich = opts?.rich ?? false;

  useEffect(() => {
    if (!charId) return;
    let alive = true;
    let retry = 500;
    let ws: WebSocket | null = null;

    const connect = () => {
      if (!alive) return;
      ws = new WebSocket(`${WS_BASE}/ws/monitor?token=${encodeURIComponent(authToken())}`);
      ws.onopen = () => {
        retry = 500;
        try {
          ws?.send(JSON.stringify({ op: rich ? 'watchRich' : 'watch', charId }));
        } catch { /* noop */ }
      };
      ws.onmessage = (e) => {
        try {
          const m = JSON.parse(e.data);
          if (!m) return;
          if (m.t === 'pos' || m.t === 'rich') onPosRef.current(m);
          else if (m.t === 'hist') onHistRef.current?.(m);
          else if (m.t === 'alert' && m.alert) onAlertRef.current?.(m.alert);
        } catch { /* frame invalido — ignora */ }
      };
      ws.onclose = () => {
        if (alive) {
          setTimeout(connect, retry);
          retry = Math.min(retry * 2, 8000);
        }
      };
      ws.onerror = () => {
        try { ws?.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      alive = false;
      try { ws?.send(JSON.stringify({ op: 'unwatch', charId })); } catch { /* noop */ }
      try { ws?.close(); } catch { /* noop */ }
    };
  }, [charId, rich]);
}
